import * as _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Dialog, IconButton } from '@material-ui/core';
import { Check, FileText, Headphones, Receipt, X } from 'lucide-react';
import { IoIosAttach } from 'react-icons/io';

import Spinner from '../../spinner';
import YeulsButton from '../../yeuls-button';
import RequestViewValue from '../common/request-view-value';
import { getErrorMessage } from '../../../shared/services/error-handler';
import {
  getExpenseRequestById,
  getInvoiceRequestById,
  getSupportTicketById,
} from '../../../api/requests/requests';
import {
  PaidBy,
  VAT_RATE_BY_OPTION,
  currencyOptions,
  departmentOptions,
  expenseCategoryOptions,
  paymentTermsOptions,
} from '../common/options';
import { NumbersService } from '../../../services/numbers';

import '../Requests.scss';

const TYPE = {
  invoice: 'invoice',
  expense: 'expense',
  service: 'service',
};

const fetchers = {
  [TYPE.invoice]: getInvoiceRequestById,
  [TYPE.expense]: getExpenseRequestById,
  [TYPE.service]: getSupportTicketById,
};

const REQUEST_ID_PREFIX = {
  [TYPE.invoice]: 'INV',
  [TYPE.expense]: 'EXP',
  [TYPE.service]: 'SRV',
};

const CURRENCY_SYMBOL = { ILS: '₪', USD: '$', EUR: '€', GBP: '£' };

const getOptionLabel = (options, value, t) => {
  const option = _.find(options, { value });

  return option ? t(option.tLabel) : value || '';
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-GB') : '';

const formatMoney = (amount, currency) => {
  if (_.isNil(amount) || amount === '') return '';

  const symbol = CURRENCY_SYMBOL[currency] || '';

  return `${symbol}${NumbersService.numberWithCommas(Number(amount), true)}`;
};

const getInvoiceTotal = (request) =>
  _.sumBy(request.items, (item) => {
    const net = Number(item.quantity || 0) * Number(item.unit_price || 0);

    return net + (net * Number(item.vat_rate || VAT_RATE_BY_OPTION[item.vat_option] || 0)) / 100;
  });

const getTitle = (type, request) => {
  if (type === TYPE.invoice) return request.services_description;
  if (type === TYPE.expense) return request.description;

  return request.subject;
};

const RequestAttachments = ({ attachments, t }) => {
  if (!attachments?.length) {
    return <RequestViewValue tLabel="attachments-optional" value={t('no-attachments')} />;
  }

  return (
    <div className="request-attachment-list">
      {attachments.map((attachment) => (
        <a
          key={attachment.id}
          className="request-attachment-item request-attachment-link"
          href={attachment.file_link}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="request-attachment-name">
            <IoIosAttach />
            {attachment.file_name}
          </span>
        </a>
      ))}
    </div>
  );
};

const InvoiceBody = ({ request, t }) => (
  <>
    <section className="request-view-section">
      <h3>{t('who-is-the-client')}</h3>
      <RequestViewValue
        tLabel="client"
        value={_.get(request, 'client.client_name')}
      />
    </section>

    <section className="request-view-section">
      <h3>{t('invoice-details')}</h3>
      <div className="request-view-grid">
        <RequestViewValue tLabel="date" value={formatDate(request.created_at)} />
        <RequestViewValue
          tLabel="total"
          value={formatMoney(getInvoiceTotal(request), request.currency)}
        />
        <RequestViewValue
          tLabel="currency"
          value={getOptionLabel(currencyOptions, request.currency, t)}
        />
        <RequestViewValue
          tLabel="payment-terms-label"
          value={getOptionLabel(paymentTermsOptions, request.payment_terms, t)}
        />
        {request.is_recurring && (
          <RequestViewValue
            tLabel="recurring-day"
            value={request.recurring_day}
          />
        )}
      </div>
    </section>

    <section className="request-view-section">
      <h3>{t('invoice-notes')}</h3>
      <RequestViewValue value={request.notes} />
    </section>
  </>
);

const ExpenseBody = ({ request, t }) => (
  <>
    <section className="request-view-section">
      <h3>{t('supplier-service-provider')}</h3>
      <RequestViewValue
        tLabel="supplier"
        value={_.get(request, 'supplier.supplier_name')}
      />
    </section>

    <section className="request-view-section">
      <h3>{t('expense-details')}</h3>
      <div className="request-view-grid">
        <RequestViewValue tLabel="date" value={formatDate(request.expense_date)} />
        <RequestViewValue
          tLabel="total"
          value={formatMoney(request.total_amount, request.currency)}
        />
        <RequestViewValue
          tLabel="invoice-number"
          value={request.invoice_number}
        />
        <RequestViewValue
          tLabel="category"
          value={getOptionLabel(expenseCategoryOptions, request.category, t)}
        />
        <RequestViewValue
          tLabel="paid-by"
          value={request.paid_by === PaidBy.yeuls ? t('paid-by-yeuls') : t('paid-by-me')}
        />
      </div>
    </section>

    {request.paid_by === PaidBy.yeuls && (
      <section className="request-view-section">
        <h3>{t('bank-account-details-for-payment')}</h3>
        <div className="request-view-grid">
          <RequestViewValue
            tLabel="account-holder-name"
            value={request.account_holder_name}
          />
          <RequestViewValue tLabel="bank-name" value={request.bank_name} />
          <RequestViewValue
            tLabel="branch-number"
            value={request.bank_branch_number}
          />
          <RequestViewValue
            tLabel="account-number"
            value={request.bank_account_number}
          />
        </div>
      </section>
    )}

    <section className="request-view-section">
      <h3>{t('expense-description')}</h3>
      <RequestViewValue value={request.description} />
    </section>

    <section className="request-view-section">
      <h3>{t('attach-receipt')}</h3>
      <RequestAttachments attachments={request.attachments} t={t} />
    </section>
  </>
);

const ServiceBody = ({ request, t }) => (
  <>
    <section className="request-view-section">
      <h3>{t('ticket-details')}</h3>
      <RequestViewValue tLabel="ticket-subject" value={request.subject} />
      <RequestViewValue
        tLabel="target-department"
        value={getOptionLabel(departmentOptions, request.department, t)}
      />
    </section>

    <section className="request-view-section">
      <h3>{t('ticket-description')}</h3>
      <RequestViewValue value={request.description} />
    </section>

    <section className="request-view-section">
      <h3>{t('attachments-optional')}</h3>
      <RequestAttachments attachments={request.attachments} t={t} />
    </section>
  </>
);

const RenewalHistory = ({ steps, t }) => {
  if (!steps?.length) return null;

  return (
    <section className="request-view-section request-renewal-history">
      <h3>{t('renewal-history')}</h3>
      <div className="request-renewal-history-list">
        {steps.map((step) => (
          <div
            key={step.key}
            className={`request-renewal-history-row ${step.state}`}
          >
            <span>{t(`recurring-step-${step.state}`)}</span>
            <strong>
              {t(`recurring-month-${step.monthKey}`)} {step.year}
            </strong>
          </div>
        ))}
      </div>
    </section>
  );
};

const RequestView = (props) => {
  const { open, type, requestId, renewalHistory, onClose } = props;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(false);
  const [request, setRequest] = useState(null);

  useEffect(() => {
    if (!open || !type || !requestId) {
      setRequest(null);
      return;
    }

    let cancelled = false;

    setIsLoading(true);

    fetchers[type](requestId)
      .then((result) => {
        if (!cancelled) setRequest(result);
      })
      .catch((error) => {
        if (cancelled) return;

        enqueueSnackbar(getErrorMessage(error) || t('something-went-wrong'), {
          variant: 'error',
        });
        onClose();
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type, requestId]);

  const title = request ? getTitle(type, request) : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="request-view-dialog"
      PaperProps={{ className: 'request-view-paper' }}
    >
      {isLoading || !request ? (
        <div className="request-view-loading">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="request-view-header">
            <div className={`request-view-header-icon ${type}`}>
              {type === TYPE.expense && <Receipt size={18} />}
              {type === TYPE.service && <Headphones size={18} />}
              {type === TYPE.invoice && <FileText size={18} />}
            </div>

            <div className="request-view-header-copy">
              <div className="request-view-header-meta">
                <span className={`request-type-badge ${type}`}>
                  {t(`request-type-${type}`)}
                </span>
                {request.is_recurring && (
                  <span className="my-request-recurring-badge">
                    {t('recurring-invoice')}
                  </span>
                )}
                <span className={`my-request-status ${request.status}`}>
                  {request.status === 'done' ? (
                    <Check size={12} />
                  ) : (
                    <span className="my-request-status-dot" />
                  )}
                  {t(`request-status-${request.status}`)}
                </span>
              </div>
              <h2>{title}</h2>
              <p>
                {t('request-id')}: {REQUEST_ID_PREFIX[type]}-{request.id}
                {request.created_at ? ` · ${formatDate(request.created_at)}` : ''}
              </p>
            </div>

            <IconButton
              className="request-view-close-icon"
              onClick={onClose}
              aria-label={t('request-view-close')}
            >
              <X size={18} />
            </IconButton>
          </div>

          <div className="request-view-body">
            {type === TYPE.invoice && <InvoiceBody request={request} t={t} />}
            {type === TYPE.expense && <ExpenseBody request={request} t={t} />}
            {type === TYPE.service && <ServiceBody request={request} t={t} />}
            <RenewalHistory steps={renewalHistory} t={t} />
          </div>

          <YeulsButton className="request-view-close-button" onClick={onClose}>
            {t('request-view-close')}
          </YeulsButton>
        </>
      )}
    </Dialog>
  );
};

export default RequestView;
